/**
 * TalentTimePayments Component
 *
 * Features:
 * - Displays talent timesheet records with sorting and filtering
 * - Summary cards showing total payments, hours, and timesheet count
 * - Search, status, and date range filtering
 * - Error handling for 500, 422, and 403 status codes
 * - Empty state when no data is available
 * - Loading feedback during data fetches
 *
 * URL Parameters for testing:
 * - ?error_mode=500 - Simulate internal server error
 * - ?error_mode=422 - Simulate bad request error
 * - ?error_mode=403 - Simulate permission error
 * - ?empty=true - Show empty state
 * - ?search=text - Filter by search term
 * - ?status=paid|pending|processing - Filter by status
 * - ?min_period=MM/DD/YYYY - Filter by start date
 * - ?max_period=MM/DD/YYYY - Filter by end date
 * - ?page=1&per_page=20 - Pagination
 * - ?sort_by=field&sort_dir=asc|desc - Sorting
 */

import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DatePicker, Input, Select, Table } from "antd";
import dayjs from "dayjs";
import { useAPI } from "../../config/API";

const { Option } = Select;
const { Search } = Input;
const { RangePicker } = DatePicker;

const TalentTimePayments = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [simulatedError, setSimulatedError] = useState(null);
    const [isSimulatedLoading, setIsSimulatedLoading] = useState(false);
    const [groupBy, setGroupBy] = useState('none'); // 'none', 'user', 'project'

    // Parse search params for pagination and sorting
    const decodedSearchParams = useMemo(() => {
        return [...searchParams.entries()].reduce((acc, [key, val]) => {
            return { ...acc, [key]: val };
        }, {});
    }, [searchParams.toString()]);

    // Set default date filters on mount if not present
    useEffect(() => {
        const currentParams = new URLSearchParams(searchParams);
        let needsUpdate = false;

        if (!currentParams.has('page')) {
            currentParams.set('page', 1);
            needsUpdate = true;
        }

        if (!currentParams.has('per_page')) {
            currentParams.set('per_page', 50);
            needsUpdate = true;
        }
        // Set default min_period to beginning of current year if not set
        if (!currentParams.has('min_period')) {
            const startOfYear = dayjs().startOf('year').format('MM/DD/YYYY');
            currentParams.set('min_period', startOfYear);
            needsUpdate = true;
        }

        // Set default max_period to current date if not set
        if (!currentParams.has('max_period')) {
            const today = dayjs().format('MM/DD/YYYY');
            currentParams.set('max_period', today);
            needsUpdate = true;
        }

        if (needsUpdate) {
            console.log('now')
            requestAnimationFrame(() => setSearchParams(currentParams, { replace: true }));
        }
    }, []);

    // Simulate data fetching with loading and error states
    useEffect(() => {
        const errorMode = decodedSearchParams.error_mode;
        const shouldSimulateEmpty = decodedSearchParams.empty === 'true';

        if (errorMode || shouldSimulateEmpty) {
            setIsSimulatedLoading(true);
            setSimulatedError(null);

            const timer = setTimeout(() => {
                setIsSimulatedLoading(false);

                if (errorMode === '500') {
                    setSimulatedError({
                        status: 500,
                        message: 'Internal Server Error',
                        details: 'An unexpected error occurred on the server. Our team has been notified and is working on a fix.'
                    });
                } else if (errorMode === '422') {
                    setSimulatedError({
                        status: 422,
                        message: 'Invalid Request Data',
                        details: 'The request contains invalid parameters. Please check your filters and try again.'
                    });
                } else if (errorMode === '403') {
                    setSimulatedError({
                        status: 403,
                        message: 'Access Denied',
                        details: 'You do not have sufficient permissions to view financial data. Please contact your administrator.'
                    });
                }
            }, 1500);

            return () => clearTimeout(timer);
        } else {
            setSimulatedError(null);
        }
    }, [decodedSearchParams]);

    // API call using the useAPI hook
    // const error = false
    // const error = { code: 200, message: 'yyo'}
    const { data, isFetching, error } = useAPI([
        'fetchTalentTimePayments',
        {
            // page: decodedSearchParams.page || 1,
            // per_page: decodedSearchParams.per_page || 20,
            // sort_by: decodedSearchParams.sort_by || 'created_at',
            // sort_dir: decodedSearchParams.sort_dir || 'desc',
            min_period: decodedSearchParams.min_period || TB.moment().startOf('year').format('L'),
            max_period: decodedSearchParams.max_period || TB.moment().format('L'),
            // ...decodedSearchParams,
            // page: null,//decodedSearchParams.page || 1,
            // per_page: null,//decodedSearchParams.per_page || 20,
        }
    ], {
        enabled: searchParams?.toString()?.length > 0,
        keepPreviousData: true
    });

    // Seed data for development
    const shouldShowEmpty = decodedSearchParams.empty === 'true';

    // Calculate summary stats based on ALL filtered data (not just current page)
    const allFilteredData = useMemo(() => {
        if (shouldShowEmpty) return [];

        let filteredData = [...(data?.rows || [])];

        const searchTerm = decodedSearchParams.search?.toLowerCase();
        if (searchTerm) {
            filteredData = filteredData.filter(payment =>
                (payment.user?.first_name.toLowerCase() + ' ' + payment.user?.last_name.toLowerCase()).includes(searchTerm) ||
                payment.project?.name.toLowerCase().includes(searchTerm)
            );
        }

        const statusFilter = decodedSearchParams.status?.toLowerCase();
        if (statusFilter && statusFilter !== 'all') {
            filteredData = filteredData.filter(payment =>
                payment.type?.toLowerCase() === statusFilter
            );
        }

        return filteredData;
    }, [data, isFetching, error, shouldShowEmpty, searchParams?.toString()]);

    // Group data by User
    const groupDataByUser = (rows) => {
        const grouped = {};

        rows.forEach(row => {
            const userId = row.user?.id;
            if (!userId) return;

            if (!grouped[userId]) {
                grouped[userId] = {
                    user: row.user,
                    rows: [],
                    totalHours: 0,
                    totalPayment: 0,
                    totalPeriods: 0,
                    rates: []
                };
            }

            grouped[userId].rows.push(row);
            grouped[userId].totalHours += row.total_hours || 0;
            grouped[userId].totalPayment += parseFloat(row.rate || 0) * (row.total_hours || 0);
            grouped[userId].totalPeriods += row.periods ? Object.keys(row.periods).length : 0;
            grouped[userId].rates.push(parseFloat(row.rate || 0));
        });

        // Calculate average rate and format for table
        return Object.values(grouped).map(group => ({
            key: group.user.id,
            user: group.user,
            totalHours: group.totalHours,
            avgRate: group.rates.reduce((sum, rate) => sum + rate, 0) / group.rates.length,
            totalPayment: group.totalPayment,
            totalPeriods: group.totalPeriods,
            rows: group.rows // Store rows for nested table
        }));
    };

    // Group data by Project
    const groupDataByProject = (rows) => {
        const grouped = {};

        rows.forEach(row => {
            const projectId = row.project?.id;
            if (!projectId) return;

            if (!grouped[projectId]) {
                grouped[projectId] = {
                    project: row.project,
                    rows: [],
                    totalHours: 0,
                    totalPayment: 0,
                    totalPeriods: 0,
                    rates: [],
                    userCount: new Set()
                };
            }

            grouped[projectId].rows.push(row);
            grouped[projectId].totalHours += row.total_hours || 0;
            grouped[projectId].totalPayment += parseFloat(row.rate || 0) * (row.total_hours || 0);
            grouped[projectId].totalPeriods += row.periods ? Object.keys(row.periods).length : 0;
            grouped[projectId].rates.push(parseFloat(row.rate || 0));
            grouped[projectId].userCount.add(row.user?.id);
        });

        // Calculate average rate and format for table
        return Object.values(grouped).map(group => ({
            key: group.project.id,
            project: group.project,
            totalHours: group.totalHours,
            avgRate: group.rates.reduce((sum, rate) => sum + rate, 0) / group.rates.length,
            totalPayment: group.totalPayment,
            totalPeriods: group.totalPeriods,
            userCount: group.userCount.size,
            rows: group.rows // Store rows for nested table
        }));
    };

    // Get the appropriate data based on grouping
    const tableData = useMemo(() => {
        if (groupBy === 'user') {
            return groupDataByUser(allFilteredData);
        }
        if (groupBy === 'project') {
            return groupDataByProject(allFilteredData);
        }
        return allFilteredData;
    }, [groupBy, allFilteredData]);


    // Handle table changes (pagination, sorting)
    const handleTableChange = (pagination, filters, sorter) => {
        setIsSimulatedLoading(true);
        const newParams = new URLSearchParams(searchParams);

        // Update pagination
        if (pagination.current) {
            newParams.set('page', pagination.current.toString());
        }
        if (pagination.pageSize) {
            newParams.set('per_page', pagination.pageSize.toString());
        }

        // Update sorting
        if (sorter.field) {
            newParams.set('sort_by', sorter.field);
            newParams.set('sort_dir', sorter.order === 'ascend' ? 'asc' : 'desc');
        }

        setSearchParams(newParams);

        // Simulate loading
        setTimeout(() => {
            setIsSimulatedLoading(false);
        }, 800);
    };

    // Handle filter changes
    const handleStatusFilter = (value) => {
        const newParams = new URLSearchParams(searchParams);
        if (value && value !== 'all') {
            newParams.set('status', value);
        } else {
            newParams.delete('status');
        }
        newParams.set('page', '1'); // Reset to first page
        setSearchParams(newParams);
    };

    const handleSearch = (value) => {
        const newParams = new URLSearchParams(searchParams);
        if (value) {
            newParams.set('search', value);
        } else {
            newParams.delete('search');
        }
        newParams.set('page', '1'); // Reset to first page
        setSearchParams(newParams);
    };

    // Handle date filter changes (dates are required)
    const handleMinPeriodChange = (date) => {
        if (!date) return; // Prevent clearing the date
        const newParams = new URLSearchParams(searchParams);
        newParams.set('min_period', date.format('MM/DD/YYYY'));
        newParams.set('page', '1'); // Reset to first page
        setSearchParams(newParams);
    };

    const handleMaxPeriodChange = (date) => {
        if (!date) return; // Prevent clearing the date
        const newParams = new URLSearchParams(searchParams);
        newParams.set('max_period', date.format('MM/DD/YYYY'));
        newParams.set('page', '1'); // Reset to first page
        setSearchParams(newParams);
    };

    // Handle date range change (for RangePicker)
    const handleDateRangeChange = (dates) => {
        if (!dates || dates.length !== 2) return; // Prevent clearing the dates
        const newParams = new URLSearchParams(searchParams);
        newParams.set('min_period', dates[0].format('MM/DD/YYYY'));
        newParams.set('max_period', dates[1].format('MM/DD/YYYY'));
        newParams.set('page', '1'); // Reset to first page
        setSearchParams(newParams);
    };

    // Handle error simulation
    const handleErrorSimulation = (errorType) => {
        const newParams = new URLSearchParams(searchParams);
        if (errorType && errorType !== 'none') {
            newParams.set('error_mode', errorType);
        } else {
            newParams.delete('error_mode');
        }
        setSearchParams(newParams);
    };

    // Handle empty state simulation
    const handleEmptyToggle = () => {
        const newParams = new URLSearchParams(searchParams);
        const currentEmpty = newParams.get('empty') === 'true';
        if (!currentEmpty) {
            newParams.set('empty', 'true');
        } else {
            newParams.delete('empty');
        }
        setSearchParams(newParams);
    };

    // Helper function to get status badge styling
    const getStatusBadge = (status) => {
        const badges = {
            'Paid': 'bg-green-100 text-green-800 border border-green-200',
            'Pending': 'bg-yellow-100 text-yellow-800 border border-yellow-200',
            'Processing': 'bg-blue-100 text-blue-800 border border-blue-200',
        };
        return badges[status] || 'bg-gray-100 text-gray-800 border border-gray-200';
    };

    // Define columns for ungrouped table
    const defaultColumns = [
        {
            title: 'Talent Name',
            dataIndex: 'user',
            key: 'user',
            sorter: (a, b) => {
                const nameA = `${a.user?.first_name} ${a.user?.last_name}`.toLowerCase();
                const nameB = `${b.user?.first_name} ${b.user?.last_name}`.toLowerCase();
                return nameA.localeCompare(nameB);
            },
            width: 225,
            render: (user) => (
                <div className="flex items-center gap-2">
                    {user?.image_path && (
                        <img
                            src={user.image_path}
                            alt={`${user.first_name} ${user.last_name}`}
                            className="w-8 h-8 rounded-full object-cover"
                        />
                    )}
                    <span className="font-medium text-gray-900">
                        {user?.first_name} {user?.last_name}
                    </span>
                </div>
            ),
        },
        {
            title: 'Role',
            dataIndex: 'position',
            key: 'position',
            sorter: (a, b) => {
                const titleA = (a.position?.title || '').toLowerCase();
                const titleB = (b.position?.title || '').toLowerCase();
                return titleA.localeCompare(titleB);
            },
            width: 230,
            render: (position, record) => (
                <div className="flex flex-col gap-1">
                    <span className="text-sm text-gray-700">{position?.title}</span>
                    {record.project?.name && (
                        <span className="text-xs text-secondary">
                            Project: {record.project?.name}
                        </span>
                    )}
                </div>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            sorter: (a, b) => {
                const typeA = (a.type || '').toLowerCase();
                const typeB = (b.type || '').toLowerCase();
                return typeA.localeCompare(typeB);
            },
            render: (type) => (
                <span className="text-gray-600 text-sm capitalize">
                    {type?.replace(/-/g, ' ')}
                </span>
            ),
        },
        {
            title: 'Total Hours',
            dataIndex: 'total_hours',
            key: 'total_hours',
            sorter: (a, b) => (a.total_hours || 0) - (b.total_hours || 0),
            render: (hours) => (
                <span className="font-mono text-gray-900">{hours}h</span>
            ),
        },
        {
            title: 'Rate',
            dataIndex: 'rate',
            key: 'rate',
            sorter: (a, b) => parseFloat(a.rate || 0) - parseFloat(b.rate || 0),
            render: (rate) => (
                <span className="font-mono text-gray-900">
                    ${parseFloat(rate).toFixed(2)}/hr
                </span>
            ),
        },
        {
            title: 'Total Payment',
            dataIndex: ['rate', 'total_hours'],
            key: 'total_payment',
            sorter: (a, b) => {
                const totalA = parseFloat(a.rate || 0) * (a.total_hours || 0);
                const totalB = parseFloat(b.rate || 0) * (b.total_hours || 0);
                return totalA - totalB;
            },
            render: (_, record) => {
                const total = parseFloat(record.rate) * record.total_hours;
                return (
                    <span className="font-semibold text-gray-900">
                        ${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                );
            },
        },
        {
            title: 'Period Count',
            dataIndex: 'periods',
            key: 'periods',
            sorter: (a, b) => {
                const countA = a.periods ? Object.keys(a.periods).length : 0;
                const countB = b.periods ? Object.keys(b.periods).length : 0;
                return countA - countB;
            },
            render: (periods) => (
                <span className="text-gray-600">
                    {periods ? Object.keys(periods).length : 0} periods
                </span>
            ),
        },
    ];

    // Define columns for User grouping
    const userGroupColumns = [
        {
            title: 'Talent Name',
            dataIndex: 'user',
            key: 'user',
            width: 225,
            sorter: (a, b) => {
                const nameA = `${a.user?.first_name} ${a.user?.last_name}`.toLowerCase();
                const nameB = `${b.user?.first_name} ${b.user?.last_name}`.toLowerCase();
                return nameA.localeCompare(nameB);
            },
            render: (user) => (
                <div className="flex items-center gap-2">
                    {user?.image_path && (
                        <img
                            src={user.image_path}
                            alt={`${user.first_name} ${user.last_name}`}
                            className="w-8 h-8 rounded-full object-cover"
                        />
                    )}
                    <span className="font-medium text-gray-900">
                        {user?.first_name} {user?.last_name}
                    </span>
                </div>
            ),
        },
        {
            title: 'Total Periods',
            dataIndex: 'totalPeriods',
            width: 150,
            key: 'totalPeriods',
            sorter: (a, b) => (a.totalPeriods || 0) - (b.totalPeriods || 0),
            render: (count) => (
                <span className="text-gray-600">
                    {count} periods
                </span>
            ),
        },
        {
            title: 'Total Hours',
            dataIndex: 'totalHours',
            key: 'totalHours',
            width: 150,
            sorter: (a, b) => (a.totalHours || 0) - (b.totalHours || 0),
            render: (hours) => (
                <span className="font-mono text-gray-900 font-semibold">{hours.toFixed(1)}h</span>
            ),
        },
        {
            title: 'Avg Rate',
            dataIndex: 'avgRate',
            key: 'avgRate',
            width: 150,
            sorter: (a, b) => (a.avgRate || 0) - (b.avgRate || 0),
            render: (rate) => (
                <span className="font-mono text-gray-900">
                    ${rate.toFixed(2)}/hr
                </span>
            ),
        },
        {
            title: 'Total Payment',
            dataIndex: 'totalPayment',
            key: 'totalPayment',
            width: 180,
            sorter: (a, b) => (a.totalPayment || 0) - (b.totalPayment || 0),
            render: (amount) => (
                <span className="font-semibold text-gray-900">
                    ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
            ),
        },
    ];

    // Define columns for Project grouping
    const projectGroupColumns = [
        {
            title: 'Project Name',
            dataIndex: 'project',
            key: 'project',
            width: 250,
            sorter: (a, b) => {
                const nameA = (a.project?.name || '').toLowerCase();
                const nameB = (b.project?.name || '').toLowerCase();
                return nameA.localeCompare(nameB);
            },
            render: (project) => (
                <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">
                        {project?.name}
                    </span>
                </div>
            ),
        },
        {
            title: 'Team Members',
            dataIndex: 'userCount',
            width: 150,
            key: 'userCount',
            sorter: (a, b) => (a.userCount || 0) - (b.userCount || 0),
            render: (count) => (
                <span className="text-gray-600">
                    {count} {count === 1 ? 'member' : 'members'}
                </span>
            ),
        },
        {
            title: 'Total Periods',
            dataIndex: 'totalPeriods',
            width: 150,
            key: 'totalPeriods',
            sorter: (a, b) => (a.totalPeriods || 0) - (b.totalPeriods || 0),
            render: (count) => (
                <span className="text-gray-600">
                    {count} periods
                </span>
            ),
        },
        {
            title: 'Total Hours',
            dataIndex: 'totalHours',
            key: 'totalHours',
            width: 150,
            sorter: (a, b) => (a.totalHours || 0) - (b.totalHours || 0),
            render: (hours) => (
                <span className="font-mono text-gray-900 font-semibold">{hours.toFixed(1)}h</span>
            ),
        },
        {
            title: 'Avg Rate',
            dataIndex: 'avgRate',
            key: 'avgRate',
            width: 150,
            sorter: (a, b) => (a.avgRate || 0) - (b.avgRate || 0),
            render: (rate) => (
                <span className="font-mono text-gray-900">
                    ${rate.toFixed(2)}/hr
                </span>
            ),
        },
        {
            title: 'Total Payment',
            dataIndex: 'totalPayment',
            key: 'totalPayment',
            width: 180,
            sorter: (a, b) => (a.totalPayment || 0) - (b.totalPayment || 0),
            render: (amount) => (
                <span className="font-semibold text-gray-900">
                    ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
            ),
        },
    ];

    // Get columns based on grouping mode
    const columns = useMemo(() => {
        if (groupBy === 'user') {
            return userGroupColumns;
        }
        if (groupBy === 'project') {
            return projectGroupColumns;
        }
        return defaultColumns;
    }, [groupBy]);

    // Define nested table columns for User grouping (showing individual periods)
    const nestedColumns = [
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            width: 200,
            sorter: (a, b) => {
                const dateA = new Date(a.date);
                const dateB = new Date(b.date);
                return dateA - dateB;
            },
            render: (date) => (
                <span className="text-gray-900">
                    {TB.moment(date).format('LL')}
                </span>
            ),
        },
        {
            title: 'Role',
            dataIndex: 'role',
            width: 225,
            key: 'role',
            sorter: (a, b) => {
                const roleA = (a.role || '').toLowerCase();
                const roleB = (b.role || '').toLowerCase();
                return roleA.localeCompare(roleB);
            },
            render: (role) => (
                <span className="text-xs text-gray-700">{role}</span>
            ),
        },

        {
            title: 'Type',
            dataIndex: 'type',
            width: 200,
            key: 'type',
            sorter: (a, b) => {
                const typeA = (a.type || '').toLowerCase();
                const typeB = (b.type || '').toLowerCase();
                return typeA.localeCompare(typeB);
            },
            render: (type) => (
                <span className="text-gray-600 text-xs capitalize">
                    {type?.replace(/-/g, ' ')}
                </span>
            ),
        },
        {
            title: 'Hours',
            dataIndex: 'hours',
            key: 'hours',
            width: 200,
            sorter: (a, b) => parseFloat(a.hours || 0) - parseFloat(b.hours || 0),
            render: (hours) => (
                <span className="font-mono text-gray-900">{hours}h</span>
            ),
        },
        {
            title: 'Rate',
            dataIndex: 'rate',
            key: 'rate',
            width: 200,
            sorter: (a, b) => parseFloat(a.rate || 0) - parseFloat(b.rate || 0),
            render: (rate) => (
                <span className="font-mono text-gray-900">
                    ${parseFloat(rate).toFixed(2)}/hr
                </span>
            ),
        },
        {
            title: 'Payment',
            key: 'payment',
            sorter: (a, b) => {
                const totalA = parseFloat(a.rate || 0) * parseFloat(a.hours || 0);
                const totalB = parseFloat(b.rate || 0) * parseFloat(b.hours || 0);
                return totalA - totalB;
            },
            render: (_, record) => {
                const total = parseFloat(record.rate) * parseFloat(record.hours);
                return (
                    <span className="font-semibold text-gray-900">
                        ${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                );
            },
        },
    ];

    // Define nested table columns for Project grouping (showing users on the project)
    const nestedProjectColumns = [
        {
            title: 'Talent Name',
            dataIndex: 'user',
            key: 'user',
            width: 225,
            sorter: (a, b) => {
                const nameA = `${a.user?.first_name} ${a.user?.last_name}`.toLowerCase();
                const nameB = `${b.user?.first_name} ${b.user?.last_name}`.toLowerCase();
                return nameA.localeCompare(nameB);
            },
            render: (user) => (
                <div className="flex items-center gap-2">
                    {user?.image_path && (
                        <img
                            src={user.image_path}
                            alt={`${user.first_name} ${user.last_name}`}
                            className="w-8 h-8 rounded-full object-cover"
                        />
                    )}
                    <span className="font-medium text-gray-900">
                        {user?.first_name} {user?.last_name}
                    </span>
                </div>
            ),
        },
        {
            title: 'Role',
            dataIndex: 'role',
            width: 225,
            key: 'role',
            sorter: (a, b) => {
                const roleA = (a.role || '').toLowerCase();
                const roleB = (b.role || '').toLowerCase();
                return roleA.localeCompare(roleB);
            },
            render: (role) => (
                <span className="text-sm text-gray-700">{role}</span>
            ),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            width: 200,
            key: 'type',
            sorter: (a, b) => {
                const typeA = (a.type || '').toLowerCase();
                const typeB = (b.type || '').toLowerCase();
                return typeA.localeCompare(typeB);
            },
            render: (type) => (
                <span className="text-gray-600 text-sm capitalize">
                    {type?.replace(/-/g, ' ')}
                </span>
            ),
        },
        {
            title: 'Total Hours',
            dataIndex: 'totalHours',
            key: 'totalHours',
            width: 150,
            sorter: (a, b) => parseFloat(a.totalHours || 0) - parseFloat(b.totalHours || 0),
            render: (hours) => (
                <span className="font-mono text-gray-900">{hours}h</span>
            ),
        },
        {
            title: 'Rate',
            dataIndex: 'rate',
            key: 'rate',
            width: 150,
            sorter: (a, b) => parseFloat(a.rate || 0) - parseFloat(b.rate || 0),
            render: (rate) => (
                <span className="font-mono text-gray-900">
                    ${parseFloat(rate).toFixed(2)}/hr
                </span>
            ),
        },
        {
            title: 'Total Payment',
            key: 'totalPayment',
            width: 180,
            sorter: (a, b) => {
                const totalA = parseFloat(a.rate || 0) * parseFloat(a.totalHours || 0);
                const totalB = parseFloat(b.rate || 0) * parseFloat(b.totalHours || 0);
                return totalA - totalB;
            },
            render: (_, record) => {
                const total = parseFloat(record.rate) * parseFloat(record.totalHours);
                return (
                    <span className="font-semibold text-gray-900">
                        ${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                );
            },
        },
        {
            title: 'Period Count',
            dataIndex: 'periodCount',
            key: 'periodCount',
            width: 150,
            sorter: (a, b) => (a.periodCount || 0) - (b.periodCount || 0),
            render: (count) => (
                <span className="text-gray-600">
                    {count} periods
                </span>
            ),
        },
    ];

    // Expandable row render for User and Project grouping
    const expandedRowRender = (record) => {
        if (groupBy === 'user') {
            // Transform rows into individual period rows
            let periodRows = [];

            record.rows.forEach((row) => {
                const periods = row.periods || {};

                // Create a row for each period
                TB.map(periods, (hours, date) => {
                    periodRows.push({
                        role: row.position?.title,
                        date: date,
                        type: row.type,
                        hours: hours,
                        rate: row.rate,
                        // Add unique identifiers for the key
                        _parentId: row.user?.id,
                        _projectId: row.project?.id,
                        _positionId: row.position?.id,
                    });
                });
            });
            periodRows = TB.orderBy(periodRows, 'date', 'asc')
            return (
                <Table
                    columns={nestedColumns}
                    dataSource={periodRows}
                    pagination={false}
                    rowKey={(row, index) => `${row.date + row._parentId}-${row._projectId}-${row._positionId}-${row.date}-${index}`}
                    size="small"
                    className="bg-gray-50 border-l-2 border-blue-300"
                />
            );
        }

        if (groupBy === 'project') {
            // Group rows by user to show unique users with aggregated data
            const userMap = {};

            record.rows.forEach((row) => {
                const userId = row.user?.id;
                if (!userId) return;

                if (!userMap[userId]) {
                    userMap[userId] = {
                        user: row.user,
                        role: row.position?.title,
                        type: row.type,
                        totalHours: 0,
                        rate: row.rate,
                        periodCount: 0,
                        _userId: userId,
                        _projectId: row.project?.id,
                    };
                }

                userMap[userId].totalHours += row.total_hours || 0;
                userMap[userId].periodCount += row.periods ? Object.keys(row.periods).length : 0;
            });

            const userRows = Object.values(userMap);

            return (
                <Table
                    columns={nestedProjectColumns}
                    dataSource={userRows}
                    pagination={false}
                    rowKey={(row) => `${row._projectId}-${row._userId}`}
                    size="small"
                    className="bg-gray-50 border-l-2 border-green-300"
                    expandable={{
                        expandedRowRender: r => {
                            return (
                                <div className="bg-red-100 p-2">
                                    hello world
                                </div>
                            )
                        }
                    }}
                />
            );
        }

        return null;
    };

    // Error state UI
    const renderError = () => {
        const errorIcons = {
            500: (
                <svg className="w-16 h-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
            ),
            422: (
                <svg className="w-16 h-16 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            ),
            403: (
                <svg className="w-16 h-16 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
            )
        };

        const errorColors = {
            500: 'border-red-200 bg-red-50',
            422: 'border-orange-200 bg-orange-50',
            403: 'border-red-200 bg-red-50'
        };

        return (
            <div className="p-6">
                <div className={`rounded-lg border-2 ${errorColors[error.status] || 'border-gray-200 bg-gray-50'} p-12`}>
                    <div className="flex flex-col items-center justify-center text-center">
                        <div className="mb-4">
                            {errorIcons[error.status]}
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">
                            {error.message}
                        </h3>
                        <p className="text-gray-600 mb-6 max-w-md">
                            {error.details}
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    const newParams = new URLSearchParams(searchParams);
                                    newParams.delete('error_mode');
                                    setSearchParams(newParams);
                                }}
                                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                            >
                                Try Again
                            </button>
                            {error.status === 403 && (
                                <button
                                    onClick={() => alert('Contact administrator feature would go here')}
                                    className="px-6 py-2 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                >
                                    Contact Support
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Empty state UI
    const renderEmptyState = () => (
        <div className="p-6">
            <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-12">
                <div className="flex flex-col items-center justify-center text-center">
                    <svg className="w-20 h-20 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        No Payment Records Found
                    </h3>
                    <p className="text-gray-600 mb-6 max-w-md">
                        There are no talent payment records to display. Try adjusting your filters or check back later when new payments are processed.
                    </p>
                    <button
                        onClick={() => {
                            const newParams = new URLSearchParams();
                            // Set required date defaults
                            newParams.set('min_period', dayjs().startOf('year').format('MM/DD/YYYY'));
                            newParams.set('max_period', dayjs().format('MM/DD/YYYY'));
                            setSearchParams(newParams);
                        }}
                        className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                    >
                        Clear All Filters
                    </button>
                </div>
            </div>
        </div>
    );

    // Skeleton loader UI
    const renderSkeletonLoader = () => (
        <div className="talent-time-payments">
            {/* Header with Skeleton Summary Cards */}
            <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-start mb-6">
                    <div className="h-8 w-64 bg-gray-200 rounded animate-pulse"></div>
                </div>

                {/* Skeleton Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[1, 2, 3].map((card) => (
                        <div key={card} className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg p-5 border border-gray-300 animate-pulse">
                            <div className="flex items-center justify-between">
                                <div className="flex-1">
                                    <div className="h-4 w-24 bg-gray-300 rounded mb-3"></div>
                                    <div className="h-8 w-32 bg-gray-300 rounded"></div>
                                </div>
                                <div className="bg-gray-300 rounded-full p-3 w-12 h-12"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Skeleton Filter Controls */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <div className="flex flex-wrap gap-4 items-center">
                    <div className="h-8 w-40 bg-gray-200 rounded animate-pulse"></div>
                    <div className="flex-1 min-w-[200px]">
                        <div className="h-8 w-full bg-gray-200 rounded animate-pulse"></div>
                    </div>
                    <div className="h-8 w-40 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-8 w-40 bg-gray-200 rounded animate-pulse"></div>
                    <div className="h-8 w-48 bg-gray-200 rounded animate-pulse"></div>
                </div>
            </div>

            {/* Skeleton Table */}
            <div className="py-6 px-4">
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    {/* Table Header */}
                    <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
                        <div className="flex gap-4">
                            <div className="h-4 w-32 bg-gray-300 rounded animate-pulse"></div>
                            <div className="h-4 w-40 bg-gray-300 rounded animate-pulse"></div>
                            <div className="h-4 w-24 bg-gray-300 rounded animate-pulse"></div>
                            <div className="h-4 w-28 bg-gray-300 rounded animate-pulse"></div>
                            <div className="h-4 w-24 bg-gray-300 rounded animate-pulse"></div>
                            <div className="h-4 w-32 bg-gray-300 rounded animate-pulse"></div>
                            <div className="h-4 w-28 bg-gray-300 rounded animate-pulse"></div>
                        </div>
                    </div>

                    {/* Table Rows */}
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((row) => (
                        <div key={row} className="border-b border-gray-200 px-4 py-4">
                            <div className="flex gap-4 items-center">
                                {/* Avatar + Name */}
                                <div className="flex items-center gap-2 w-32">
                                    <div className="w-8 h-8 bg-gray-200 rounded-full animate-pulse"></div>
                                    <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                                </div>
                                {/* Role + Project */}
                                <div className="w-40">
                                    <div className="h-3 w-32 bg-gray-200 rounded mb-2 animate-pulse"></div>
                                    <div className="h-3 w-24 bg-gray-200 rounded animate-pulse"></div>
                                </div>
                                {/* Type */}
                                <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                                {/* Hours */}
                                <div className="h-4 w-16 bg-gray-200 rounded animate-pulse"></div>
                                {/* Rate */}
                                <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                                {/* Total Payment */}
                                <div className="h-4 w-28 bg-gray-200 rounded animate-pulse"></div>
                                {/* Period Count */}
                                <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Skeleton Pagination */}
                <div className="mt-4 flex justify-between items-center">
                    <div className="h-8 w-40 bg-gray-200 rounded animate-pulse"></div>
                    <div className="flex gap-2">
                        <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                        <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                        <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                        <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Show skeleton loader on initial load (when there's no data yet and we're fetching)
    if (isFetching && !data) {
        console.log('hitting loader')
        return renderSkeletonLoader();
    }

    if (error) {
        console.log('hitting error')
        return (
            <div className="talent-time-payments">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900">Talent Time & Payments</h2>
                </div>
                {renderError()}
            </div>
        );
    }



    const totalPayments = allFilteredData.reduce((sum, record) => {
        const total = parseFloat(record.rate) * record.total_hours;
        return sum + total;
    }, 0) || 0;
    const totalHours = allFilteredData.reduce((sum, record) => sum + record.total_hours, 0) || 0;
    const paidCount = allFilteredData.length || 0;

    // Support both new structure (rows) and old structure (talent_payments)
    const isEmpty = allFilteredData.length === 0;

    console.log('TalentTimePayments', { data, allFilteredData, isEmpty })



    return (
        <div className="talent-time-payments">

            {/* Header with Summary Cards */}
            <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-start mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">Talent Time & Payments</h2>

                    {/* Development Controls */}
                    <div className="flex gap-2">
                        <Select
                            placeholder="Simulate Error"
                            style={{ width: 180 }}
                            onChange={handleErrorSimulation}
                            value={decodedSearchParams.error_mode || 'none'}
                            size="small"
                        >
                            <Option value="none">No Error</Option>
                            <Option value="500">500 Server Error</Option>
                            <Option value="422">422 Bad Request</Option>
                            <Option value="403">403 Forbidden</Option>
                        </Select>
                        <button
                            onClick={handleEmptyToggle}
                            className={`px-3 py-1 text-xs rounded border transition-colors ${shouldShowEmpty
                                ? 'bg-yellow-100 border-yellow-300 text-yellow-800'
                                : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                                }`}
                        >
                            {shouldShowEmpty ? 'Show Data' : 'Empty State'}
                        </button>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5 border border-blue-200 relative">
                        {isFetching && (
                            <div className="absolute inset-0 bg-blue-50/80 rounded-lg flex items-center justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                        )}
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-blue-600 mb-1">Total Payments</p>
                                <p className="text-3xl font-bold text-blue-900">
                                    ${totalPayments.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </p>
                            </div>
                            <div className="bg-blue-200 rounded-full p-3">
                                <svg className="w-6 h-6 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-5 border border-green-200 relative">
                        {isFetching && (
                            <div className="absolute inset-0 bg-green-50/80 rounded-lg flex items-center justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                            </div>
                        )}
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-green-600 mb-1">Total Hours</p>
                                <p className="text-3xl font-bold text-green-900">
                                    {totalHours.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}h
                                </p>
                            </div>
                            <div className="bg-green-200 rounded-full p-3">
                                <svg className="w-6 h-6 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-5 border border-purple-200 relative">
                        {isFetching && (
                            <div className="absolute inset-0 bg-purple-50/80 rounded-lg flex items-center justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                            </div>
                        )}
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-purple-600 mb-1">Total Timesheets</p>
                                <p className="text-3xl font-bold text-purple-900">
                                    {allFilteredData?.length}
                                </p>
                            </div>
                            <div className="bg-purple-200 rounded-full p-3">
                                <svg className="w-6 h-6 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            {/* Filter Controls */}
            <div className={`px-6 py-4 bg-gray-50 border-b border-gray-200 transition-opacity ${isFetching ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
                <div className="flex flex-wrap gap-4 items-center">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 whitespace-nowrap font-medium">Group by:</span>
                        <Select
                            value={groupBy}
                            onChange={setGroupBy}
                            style={{ width: 150 }}
                            disabled={isFetching}
                        >
                            <Option value="none">None</Option>
                            <Option value="user">User</Option>
                            <Option value="project">Project</Option>
                        </Select>
                    </div>
                    <div className="flex-1 min-w-[200px]">
                        <Search
                            placeholder="Search by talent or project name..."
                            allowClear
                            onSearch={handleSearch}
                            defaultValue={decodedSearchParams.search}
                            className="w-full"
                            disabled={isFetching}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 whitespace-nowrap">
                            Select Dates: <span className="text-red-500">*</span>
                        </span>
                        <RangePicker
                            format="MM/DD/YYYY"
                            onChange={handleDateRangeChange}
                            value={
                                decodedSearchParams.min_period && decodedSearchParams.max_period
                                    ? [
                                        TB.moment(decodedSearchParams.min_period, 'MM/DD/YYYY'),
                                        TB.moment(decodedSearchParams.max_period, 'MM/DD/YYYY')
                                    ]
                                    : null
                            }
                            style={{ width: 300 }}
                            disabled={isFetching}
                            ranges={{
                                Today: [TB.moment(), TB.moment()],
                                // 'This Month': [TB.moment().startOf('month'), TB.moment().endOf('month')],
                                'Last Week': [TB.moment().add(-1, 'weeks').startOf('week'), TB.moment().add(-1, 'weeks').endOf('week')],
                                'Last Month': [TB.moment().add(-1, 'months').startOf('month'), TB.moment().add(-1, 'months').endOf('month')],
                                'Last Quarter': [TB.moment().add(-1, 'quarters').startOf('quarter'), TB.moment().add(-1, 'quarters').endOf('quarter')],
                                'Year to Date': [TB.moment().startOf('year'), TB.moment().endOf('month')],
                                'Last Year': [TB.moment().add(-1, 'years').startOf('year'), TB.moment().add(-1, 'years').endOf('year')],
                            }}
                        />
                    </div>
                    {/* <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 whitespace-nowrap">
                            Start Date: <span className="text-red-500">*</span>
                        </span>
                        <DatePicker
                            placeholder="Select start date"
                            format="MM/DD/YYYY"
                            onChange={handleMinPeriodChange}
                            value={decodedSearchParams.min_period ? TB.moment(decodedSearchParams.min_period, 'MM/DD/YYYY') : null}
                            // value={decodedSearchParams.min_period ? dayjs(decodedSearchParams.min_period, 'MM/DD/YYYY') : null}
                            style={{ width: 150 }}
                            disabled={isFetching}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 whitespace-nowrap">
                            End Date: <span className="text-red-500">*</span>
                        </span>
                        <DatePicker
                            placeholder="Select end date"
                            format="MM/DD/YYYY"
                            onChange={handleMaxPeriodChange}
                            value={decodedSearchParams.max_period ? TB.moment(decodedSearchParams.max_period, 'MM/DD/YYYY') : null}
                            style={{ width: 150 }}
                            disabled={isFetching}
                        />
                    </div> */}
                    <Select
                        placeholder="Filter by Type"
                        style={{ width: 200 }}
                        onChange={handleStatusFilter}
                        value={decodedSearchParams.status || 'all'}
                        allowClear
                        disabled={isFetching}
                    >
                        <Option value="all">All Types</Option>
                        <Option value="regular-hours">Regular Hours</Option>
                        <Option value="salary">Salary</Option>
                        <Option value="expense">Expense</Option>
                        <Option value="benefits-charge">Benefits Charge</Option>
                        <Option value="miscellaneous-charge">Miscellaneous Charge</Option>
                        <Option value="over-time-hours">Over Time Hours</Option>
                    </Select>
                </div>

                {/* Active Filters Display */}
                {(decodedSearchParams.search || (decodedSearchParams.status && decodedSearchParams.status !== 'all')) && (
                    <div className="flex flex-wrap gap-2 mt-3">
                        <span className="text-xs text-gray-600 font-medium">Active filters:</span>
                        {decodedSearchParams.search && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md border border-blue-200">
                                Search: "{decodedSearchParams.search}"
                                <button
                                    onClick={() => {
                                        const newParams = new URLSearchParams(searchParams);
                                        newParams.delete('search');
                                        setSearchParams(newParams);
                                    }}
                                    className="hover:text-blue-900"
                                >
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </span>
                        )}
                        {decodedSearchParams.status && decodedSearchParams.status !== 'all' && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-md border border-green-200">
                                Status: {decodedSearchParams.status.charAt(0).toUpperCase() + decodedSearchParams.status.slice(1)}
                                <button
                                    onClick={() => {
                                        const newParams = new URLSearchParams(searchParams);
                                        newParams.delete('status');
                                        setSearchParams(newParams);
                                    }}
                                    className="hover:text-green-900"
                                >
                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                    </svg>
                                </button>
                            </span>
                        )}
                        <button
                            onClick={() => {
                                const newParams = new URLSearchParams(searchParams);
                                newParams.delete('search');
                                newParams.delete('status');
                                // Reset dates to defaults instead of removing them
                                newParams.set('min_period', dayjs().startOf('year').format('MM/DD/YYYY'));
                                newParams.set('max_period', dayjs().format('MM/DD/YYYY'));
                                newParams.set('page', '1');
                                setSearchParams(newParams);
                            }}
                            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                            Clear all
                        </button>
                    </div>
                )}
            </div>

            {/* Table Section or Empty State */}
            {isEmpty ? (
                renderEmptyState()
            ) : (
                <div className="py-6 px-4">
                    {/* Loading Overlay */}
                    {isFetching && (
                        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-3">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                            <span className="text-sm text-blue-800 font-medium">Loading payment data...</span>
                        </div>
                    )}

                    {/* Results Count */}
                    {(decodedSearchParams.search || decodedSearchParams.status) && tableData?.length && (
                        <div className="mb-4 text-sm text-gray-600">
                            Showing <span className="font-semibold text-gray-900">{tableData.length}</span> results
                            {' '} out of <span className="font-semibold text-gray-900">{data?.length}</span> total
                        </div>
                    )}

                    <Table
                        columns={columns}
                        dataSource={tableData}
                        loading={isFetching}
                        pagination={{
                            current: parseInt(decodedSearchParams.page) || 1,
                            pageSize: parseInt(decodedSearchParams.per_page) || 20,
                            total: tableData.length || 0,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} of ${total} timesheets`,
                        }}
                        onChange={handleTableChange}
                        rowKey={(record, index) => record.key || record.id || `${record.user?.id}-${record.project?.id}-${record.position?.id}-${record.type}-${record.index}`}
                        scroll={{ x: 1400 }}
                        className="shadow-sm"
                        // Show current sort state in table headers
                        showSorterTooltip={false}
                        sortDirections={['ascend', 'descend', 'ascend']}
                        // Expandable rows for grouping
                        expandable={groupBy !== 'none' ? {
                            expandedRowRender,
                            defaultExpandAllRows: false,
                        } : undefined}
                    />
                </div>
            )}
        </div>
    );
};

export default TalentTimePayments;